import { useEffect, useState, useContext } from "react";
import {
  Card,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CreateContractReq from "../../../Classes/Models/Requests/Accounting/CreateContractReq";
import AccountingApi from "../../../Classes/API/AccountingApi";
import DataTable from "examples/Tables/DataTable";
import Pagination from "@mui/material/Pagination";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "App";
import SearchStringOrIntReq2 from "../../../Classes/Models/Requests/SearchStringOrIntReq";
import GetContractReportReq from "../../../Classes/Models/Requests/Accounting/GetContractReportReq";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ContractsSearch from "./ContractsSearch";
import Loader from "Loader";
import DateRangePopup from "../ContractDetails/DateRangePopup";
import { downloadContractReportExcel } from "../ContractExcelExport";

const index = () => {
  const navigate = useNavigate();

  const [SearchReq, setSearchReq] = useState(new CreateContractReq());
  const [ResponseData, setResponseData] = useState({});
  const [IsLoaded, setIsLoaded] = useState(false);
  const [TableDataRows, setTableDataRows] = useState([]);
  const { translations, switchLanguage } = useContext(LanguageContext);
  const [Page, setPage] = useState(1);
  const [TotalPages, setTotalPages] = useState(0);

  const [showDatePopup, setShowDatePopup] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);

  const [ShowArchived, setShowArchived] = useState(false);

  const TableColumns = [
    {
      Header: translations.sessionWithoutEnteriesid,
      accessor: "Id",
      align: "left",
    },
    {
      Header: translations.contactssearchName,
      accessor: "Name",
      align: "left",
    },
    {
      Header: translations.contactssearchDescription,
      accessor: "Description",
      align: "left",
    },
    {
      Header: translations.contactssearchDetails,
      accessor: "Details",
      align: "left",
    },
    {
      Header: translations.contactsDownloadDetails,
      accessor: "Download",
      align: "left",
    },
    { Header: translations.contactsStatus, accessor: "Status", align: "left" },
    {
      Header: translations.contactsActions,
      accessor: "Actions",
      align: "left",
    },
  ];

  const getContractReport = async (id, startDate = null, endDate = null) => {
    const SearchStringOrIntReq = new GetContractReportReq();
    SearchStringOrIntReq.ContractId = id;

    if (startDate) {
      SearchStringOrIntReq.Date.StartDate = startDate;
    }
    if (endDate) {
      SearchStringOrIntReq.Date.EndDate = endDate;
    }
    const res = await AccountingApi.GetContractReport(SearchStringOrIntReq);
    downloadContractReportExcel(res);
  };

  const archiveContract = async (contract) => {
    const result = await Swal.fire({
      title: translations.contactsArchiveConfirm,
      text: translations.contactsArchiveConfirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: translations.yesArchive,
      cancelButtonText: translations.cancel,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      Swal.fire({
        title: translations.processing,
        text: translations.archivingContract,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Archive the contract
      const response = await AccountingApi.ArchiveContract(contract.id);

      if (response.status === "Success") {
        await Swal.fire(
          translations.archived,
          translations.contractArchivedSuccess,
          "success"
        );
        onSearch(); // Refresh the table
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Archive error:", error);
      Swal.fire(translations.error, translations.contractArchiveError, "error");
    }
  };

  const unarchiveContract = async (contract) => {
    const result = await Swal.fire({
      title: translations.contactsUnarchiveConfirm,
      text: translations.contactsUnarchiveConfirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: translations.yesUnarchive,
      cancelButtonText: translations.cancel,
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: translations.processing,
          text: translations.unarchivingContract,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Unarchive the contract
        const response = await AccountingApi.UnarchiveContract(contract.id);

        if (response.status === "Success") {
          Swal.fire(
            translations.unarchived,
            translations.contractUnarchivedSuccess,
            "success"
          );
          onSearch(); // Refresh the table
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error("Unarchive error:", error);
        Swal.fire(
          translations.error,
          translations.contractUnarchiveError,
          "error"
        );
      }
    }
  };

  const deleteContract = async (contract) => {
    try {
      const result = await Swal.fire({
        title: translations.contactsDeleteConfirm,
        text: translations.contactsDeleteConfirmText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: translations.yesDelete,
        cancelButtonText: translations.cancel,
      });

      if (!result.isConfirmed) return;

      Swal.fire({
        title: translations.processing,
        text: translations.deletingContract,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await AccountingApi.DeleteContract(contract.id);

      if (response.status === "Success") {
        Swal.fire(
          translations.deleted,
          translations.contractDeletedSuccess,
          "success"
        );
        onSearch();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire(
        translations.error,
        error.message || translations.contractDeleteError,
        "error"
      );
    }
  };

  const handleDownloadClick = (contractId) => {
    setSelectedContractId(contractId);
    setShowDatePopup(true);
  };

  const handleDateRangeConfirm = async (startDate, endDate) => {
    await getContractReport(selectedContractId, startDate, endDate);
  };

  const handlePopupClose = () => {
    setShowDatePopup(false);
    setSelectedContractId(null);
  };

  const MakeTableRow = (Contract) => {
    const isArchived = Contract.isArchived || false;
    return {
      Id: (
        <MDTypography
          component="p"
          variant="button"
          color="text"
          fontWeight="medium"
        >
          {Contract.id}
        </MDTypography>
      ),
      Name: (
        <MDTypography
          component="p"
          variant="button"
          color="text"
          fontWeight="medium"
        >
          {Contract.name}
        </MDTypography>
      ),
      Description: (
        <MDTypography
          component="p"
          variant="button"
          color="text"
          fontWeight="medium"
        >
          {Contract.description}
        </MDTypography>
      ),
      Details: (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => {
            navigate("/ContractTable/Details", {
              state: { SearchContract: Contract },
            });
          }}
        >
          {translations.contactssearchDetails}
        </MDButton>
      ),
      Download: (
        <MDButton
          variant="gradient"
          color="success"
          size="small"
          onClick={() => handleDownloadClick(Contract.id)}
        >
          {translations.contactsDownloadDetails}
        </MDButton>
      ),
      Status: (
        <MDTypography
          component="p"
          variant="button"
          color={isArchived ? "error" : "success"}
          fontWeight="medium"
        >
          {isArchived ? translations.archived : translations.active}
        </MDTypography>
      ),
      Actions: (
        <div className="d-flex gap-2">
          {isArchived ? (
            <button
              className="btn btn-success btn-sm"
              onClick={() => unarchiveContract(Contract)}
              title={translations.unarchiveContract}
            >
              {translations.unarchive}
            </button>
          ) : (
            <button
              className="btn btn-warning btn-sm"
              onClick={() => archiveContract(Contract)}
              title={translations.archiveContract}
            >
              {translations.archive}
            </button>
          )}
          <button
            className="btn btn-danger btn-sm"
            onClick={() => deleteContract(Contract)}
            title={translations.deleteContract}
          >
            {translations.delete}
          </button>
        </div>
      ),
      Status: (
        <MDTypography
          component="p"
          variant="button"
          color={isArchived ? "error" : "success"}
          fontWeight="medium"
        >
          {isArchived ? translations.archived : translations.active}
        </MDTypography>
      ),
      Actions: (
        <div className="d-flex gap-2">
          {isArchived ? (
            <button
              className="btn btn-success btn-sm"
              onClick={() => unarchiveContract(Contract)}
              title={translations.unarchiveContract}
            >
              {translations.unarchive}
            </button>
          ) : (
            <button
              className="btn btn-warning btn-sm"
              onClick={() => archiveContract(Contract)}
              title={translations.archiveContract}
            >
              {translations.archive}
            </button>
          )}
          <button
            className="btn btn-danger btn-sm"
            onClick={() => deleteContract(Contract)}
            title={translations.deleteContract}
          >
            {translations.delete}
          </button>
        </div>
      ),
    };
  };

  const RefreshTable = (resList) => {
    const rows = [];
    resList.forEach((Entry) => {
      rows.push(MakeTableRow(Entry));
    });
    setTableDataRows(rows);
  };

  useEffect(() => {
    if (Object.keys(ResponseData).length > 0) {
      RefreshTable(ResponseData.list);
    }
  }, [ResponseData]);

  useEffect(() => {
    if (Object.keys(ResponseData).length > 0) {
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
    }
  }, [ResponseData]);

  const onSearch = async () => {
    try {
      setIsLoaded(false);

      // Create search request with proper parameters
      // By default, we don't show archived contracts unless explicitly requested
      const searchRequest = {
        ...SearchReq,
        pagingParams: {
          ...SearchReq.pagingParams,
          pageNumber: Page,
        },
        showArchived: ShowArchived,
      };

      console.log("Search request:", searchRequest);

      // Call the API to search contracts
      let res = await AccountingApi.SearchContracts(searchRequest);

      if (res.status === "Success") {
        setResponseData(res.data);
        setTotalPages(res.data.totalPages);
        RefreshTable(res.data.list);
      } else {
        console.error("Search failed:", res.message);
        setResponseData({});
        setTotalPages(0);
        setTableDataRows([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      Swal.fire(translations.error, translations.searchError, "error");
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    onSearch();
  }, [SearchReq, ShowArchived, Page]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ContractsSearch setSearchReq={setSearchReq} />

      {/* Show Archived Checkbox */}
      <Card className="mt-3">
        <MDBox p={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ShowArchived}
                onChange={(e) => {
                  setShowArchived(e.target.checked);
                  setPage(1);
                }}
                color="primary"
              />
            }
            label={translations.showArchivedContracts}
          />
        </MDBox>
      </Card>

      {!IsLoaded ? (
        <Loader />
      ) : (
        <Card className="mt-3">
          <MDBox
            py={3}
            px={2}
            sx={{
              backgroundColor: "#3560AD",

              borderTopLeftRadius: "1.1rem",

              borderTopRightRadius: "1.1rem",
            }}
            coloredShadow="info"
          >
            <div className="text-center">
              <MDTypography variant="h6" color="white">
                {ShowArchived
                  ? translations.archivedContracts
                  : translations.activeContracts}
              </MDTypography>
            </div>
          </MDBox>

          {TableDataRows.length > 0 ? (
            <>
              <DataTable
                table={{ columns: TableColumns, rows: TableDataRows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
              <div className="text-right mx-3 my-4">
                <Pagination
                  count={TotalPages}
                  page={Page}
                  onChange={(e, num) => setPage(num)}
                  color="primary"
                  className="float-right"
                />
              </div>
            </>
          ) : (
            <MDBox p={4} textAlign="center">
              <MDTypography variant="h6" color="text">
                {ShowArchived
                  ? translations.noArchivedContractsFound
                  : translations.noActiveContractsFound}
              </MDTypography>
            </MDBox>
          )}
        </Card>
      )}

      <DateRangePopup
        open={showDatePopup}
        onClose={handlePopupClose}
        onConfirm={handleDateRangeConfirm}
      />
    </DashboardLayout>
  );
};

export default index;
